// Environment configuration utility
// Automatically detects environment and provides appropriate API URLs

interface EnvironmentConfig {
  apiUrl: string;
  googleClientId: string | undefined;
  isDevelopment: boolean;
  isProduction: boolean;
}

const getEnvironmentConfig = (): EnvironmentConfig => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === "development";
  const isProduction = process.env.NODE_ENV === "production";

  // Default values
  const defaultConfig = {
    isDevelopment,
    isProduction,
    googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  };

  // Check for env override from CRA
  const envUrl = process.env.REACT_APP_API_URL;

  // If environment variable is explicitly set, use it
  if (envUrl) {
    return {
      ...defaultConfig,
      apiUrl: envUrl,
    };
  }

  // Auto-detect: localhost for local development only
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      // Default to port 5001 to match server PORT, but can be overridden via REACT_APP_SERVER_PORT
      const serverPort = process.env.REACT_APP_SERVER_PORT || "5001";
      return {
        ...defaultConfig,
        apiUrl: `http://localhost:${serverPort}`,
      };
    }
  }

  // Fallback for local development
  if (isDevelopment) {
    // Default to port 5001 to match server PORT, but can be overridden via REACT_APP_SERVER_PORT
    const serverPort = process.env.REACT_APP_SERVER_PORT || "5001";
    return {
      ...defaultConfig,
      apiUrl: `http://localhost:${serverPort}`,
    };
  }

  // If we reach here in production without REACT_APP_API_URL, that's an error
  throw new Error(
    "REACT_APP_API_URL environment variable is not set! " +
      "Please configure it in Netlify's environment variables for this deployment context.",
  );
};

export const config = getEnvironmentConfig();

// Export individual values for convenience
export const { apiUrl, googleClientId, isDevelopment, isProduction } = config;

// Broadcast configuration
export const BROADCAST_MAX_LINES = Number(process.env.REACT_APP_BROADCAST_MAX_LINES || 20);

export default config;
